import { SegmentedControl } from '@teach/ui'
import type { ImageSlot } from '@/features/ai/images/lib/targetKeys'
import type { ImageQuoteView } from '@/features/ai/useImageQuote'
import type { CourseWithoutBlocks } from '@/types/course'
import { AutoPlanSummary } from './AutoPlanSummary'
import { TargetPicker } from './TargetPicker'
import type { DrawMode } from './useDrawMode'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  mode: DrawMode
  onMode: (mode: DrawMode) => void
  /** В ручной режим уже заходили — пикер смонтирован (см. `useDrawMode`). */
  manualSeen: boolean
  course: CourseWithoutBlocks
  quote: ImageQuoteView
  nothingToDraw: boolean
  /** Заказано наборов пилотов — тумблер живёт только в авто-режиме
   *  (`AutoPlanSummary`), пропы лишь проходят сквозь. */
  pilotSets: number
  onPilotSetsChange: (next: number) => void
  onPlan: (plan: string[]) => void
  initialSlot?: ImageSlot
}

/**
 * Переключатель режима и то, что каждый режим показывает: авто — числа сметы,
 * ручной — список мест. Гейты запуска сюда не спускаются: они общие и живут в
 * диалоге (`canStartDraw`), а объяснения к ним — в `DrawImagesBody`.
 */
export const DrawImagesModes = ({
  mode,
  onMode,
  manualSeen,
  course,
  quote,
  nothingToDraw,
  pilotSets,
  onPilotSetsChange,
  onPlan,
  initialSlot,
}: Props) => {
  const m = useMessages(messages)
  return (
    <>
      <SegmentedControl
        size="sm"
        value={mode}
        onChange={onMode}
        options={[
          { id: 'auto', label: m.modeAuto },
          { id: 'manual', label: m.modeManual },
        ]}
      />
      {mode === 'auto' ? (
        <AutoPlanSummary
          quote={quote}
          nothingToDraw={nothingToDraw}
          pilotSets={pilotSets}
          onPilotSetsChange={onPilotSetsChange}
        />
      ) : null}
      {/* Пикер монтируется лениво и обратно НЕ размонтируется: выбор автора
        живёт внутри него, и тернарник по режиму стирал бы отметки на каждый
        заход в «Нарисовать всё» и назад. Скрытие — `display: none`, то есть у
        спрятанного списка нет ни фокуса, ни дерева доступности. */}
      {manualSeen ? (
        <div className={mode === 'manual' ? 'max-h-[50vh] overflow-y-auto' : 'hidden'}>
          <TargetPicker
            course={course}
            lessons={course.lessons}
            quote={quote}
            onPlan={onPlan}
            initialSlot={initialSlot}
          />
        </div>
      ) : null}
    </>
  )
}
